import { useClaimReferencesCacheStore } from 'features/fact-grabbing/useClaimReferencesCacheStore';
import {
  judgmentCountsDefault,
  useReferencesByClaim,
  UseReferencesByClaimResult
} from 'features/fact-grabbing/useReferencesByClaim';
import { ReactNode, useEffect, useState } from 'react';
import { ApiClaim } from 'services/backofficeIntegration/http/endpoints/fact-grabbing/httpGetClaimsByText';

type Props = {
  claim: ApiClaim;
  children: (props: {
    judgmentCounts: UseReferencesByClaimResult['judgmentCounts'];
    claimReferences: UseReferencesByClaimResult['references'];
    isLoading: boolean;
    isError: boolean;
    referencesByClaimResultData?: UseReferencesByClaimResult;
  }) => ReactNode;
};

export const ClaimCardReferencesLoader = ({ claim, children }: Props) => {
  const { hasInCache, getFromCache, addToCache } = useClaimReferencesCacheStore();
  const [isLoadingFromCache, setIsLoadingFromCache] = useState(true);
  const [cachedReferences, setCachedReferences] = useState<UseReferencesByClaimResult | undefined>(
    undefined
  );

  const {
    mutate: fetchClaimReferences,
    data: claimReferences,
    isLoading: isLoadingClaimReferences,
    isError: isClaimReferencesError,
    reset
  } = useReferencesByClaim({
    onSuccess: data => addToCache(claim.claim, data)
  });

  // Check for cached data first
  useEffect(() => {
    if (hasInCache(claim.claim)) {
      const data = getFromCache(claim.claim);

      setCachedReferences(data);

      setIsLoadingFromCache(false);
    } else {
      setIsLoadingFromCache(false);

      fetchClaimReferences({ claim: claim.claim });
    }

    return () => {
      reset();
    };
  }, [claim.claim, fetchClaimReferences, hasInCache, getFromCache, reset]);

  // Use cached data if available, otherwise use API data
  const finalReferencesResult = cachedReferences || claimReferences;
  const judgmentCounts = finalReferencesResult?.judgmentCounts || judgmentCountsDefault;
  const finalReferences = finalReferencesResult?.references || [];

  // We're loading if either we're checking the cache or fetching from API
  const isLoading = isLoadingFromCache || (isLoadingClaimReferences && !cachedReferences);

  return (
    <>
      {children({
        judgmentCounts,
        claimReferences: finalReferences,
        isLoading,
        isError: isClaimReferencesError && !cachedReferences,
        referencesByClaimResultData: finalReferencesResult
      })}
    </>
  );
};
